<template>
  <div
    id="coachAlert"
    :class="`${$mq}Mode`"
  >
    <!-- Valid profile alert -->
    <el-alert
      v-if="profile.is_ac && !profile.is_coach_profile_valid"
      :closable="false"
      show-icon
      type="warning"
      class="mainCard profileAlert defaultCursor"
    >
      <div
        slot="title"
        class="title profileAlertTitle"
        :class="`${$mq}Mode`"
      >
        <el-row
          :gutter="2"
          type="flex"
        >
          <el-col :span="responsiveCol.one">
            <span>
              {{ $t('customAlert.coach.profile.pleaseSetProfile') }}:
            </span>
            <ul>
              <li>{{ $t('common.name') }}</li>
              <li>{{ $t('customAlert.coach.profile.profilePicture') }}</li>
              <li>{{ $t('customAlert.coach.profile.locationAtLeastOne') }}</li>
              <li>{{ $t('customAlert.coach.profile.yearOfExp') }}</li>
              <li>{{ $t('customAlert.coach.profile.languageAtLeastOne') }}</li>
              <li>{{ $t('customAlert.coach.profile.bio') }}</li>
              <li>{{ $t('customAlert.coach.profile.exp') }}</li>
              <li>{{ $t('customAlert.coach.profile.qualifications') }}</li>
            </ul>
          </el-col>
          <el-col
            class="buttonCol"
            :span="responsiveCol.two"
          >
            <el-button
              round
              @click="goToCoachProfile"
            >
              <div style="word-break: break-word; white-space: normal;">
                {{ $t('customAlert.coach.profile.goToMyProfile') }}
              </div>
            </el-button>
          </el-col>
        </el-row>
      </div>
    </el-alert>

    <!-- Attendances alert -->
    <el-alert
      v-if="hasAttendances"
      :closable="false"
      show-icon
      type="warning"
      class="mainCard attendancesAlert"
    >
      <div
        slot="title"
        class="title"
        :class="`${$mq}Mode`"
      >
        <el-row
          :gutter="2"
          type="flex"
        >
          <el-col :span="responsiveCol.one">
            <span>
              {{ $t('customAlert.coach.attendance.pleaseComplete') }}:
            </span>
          </el-col>
          <el-col
            class="buttonCol"
            :span="responsiveCol.two"
          >
            <el-button
              round
              @click="showAttendancesList = !showAttendancesList"
            >
              {{ showAttendancesList
                ? $t('customAlert.coach.hideList') : $t('customAlert.coach.viewList') }}
            </el-button>
          </el-col>
        </el-row>
      </div>
      <div
        class="content"
      >
        <ul v-if="showAttendancesList">
          <li
            v-for="(attendance, key) in attendances"
            :key="key"
            @click="goToLeader(attendance.leader_uuid, 'attendance')"
          >
            {{ attendance.leader_name }}
          </li>
        </ul>
      </div>
    </el-alert>

    <!-- Cancellations alert -->
    <el-alert
      v-if="hasCancellations"
      :closable="false"
      show-icon
      type="warning"
      class="mainCard cancellationsAlert"
    >
      <div
        slot="title"
        class="title"
        :class="`${$mq}Mode`"
      >
        <el-row
          :gutter="2"
          type="flex"
        >
          <el-col :span="responsiveCol.one">
            <span>
              {{ $t('customAlert.coach.cancellations.pleaseComplete') }}
            </span>
          </el-col>
          <el-col
            class="buttonCol"
            :span="responsiveCol.two"
          >
            <el-button
              round
              @click="showCancellationsList = !showCancellationsList"
            >
              {{ showCancellationsList
                ? $t('customAlert.coach.hideList') : $t('customAlert.coach.viewList') }}
            </el-button>
          </el-col>
        </el-row>
      </div>
      <div
        class="content"
      >
        <ul v-if="showCancellationsList">
          <li
            v-for="(cancellation, key) in cancellations"
            :key="key"
            @click="handleCancellationClick(cancellation)"
          >
            {{ cancellation.leader_name }}
          </li>
        </ul>
      </div>
    </el-alert>

    <!-- Pulse launch alert -->
    <el-alert
      v-if="hasPulseLaunch"
      show-icon
      type="warning"
      class="mainCard pulseAlert defaultCursor"
    >
      <div
        slot="title"
        class="title"
        :class="`${$mq}Mode`"
      >
        <el-row
          :gutter="2"
          type="flex"
        >
          <el-col :span="responsiveCol.one">
            <span>
              {{ $t('customAlert.coach.pulse.pleaseLaunch') }}
            </span>
          </el-col>
          <el-col
            class="buttonCol"
            :span="responsiveCol.two"
          >
            <el-button
              round
              @click="showPulseList = !showPulseList"
            >
              {{ showPulseList
                ? $t('customAlert.coach.hideList') : $t('customAlert.coach.viewList') }}
            </el-button>
          </el-col>
        </el-row>
      </div>
      <div
        class="content"
      >
        <ul v-if="showPulseList">
          <li
            v-for="(leader, key) in pulseLaunch"
            :key="key"
            @click="goToPulse(leader.uuid)"
          >
            {{ leader.first_name }} {{ leader.last_name }}
          </li>
        </ul>
      </div>
    </el-alert>

    <!-- Subscription -->
    <el-alert
      v-if="hasInviteResetNegative || hasInviteResetPositive"
      show-icon
      type="warning"
      class="mainCard subscriptionAlert defaultCursor"
      :class="{
        subscriptionNegativeAlert: hasInviteResetNegative,
        subscriptionPositiveAlert: hasInviteResetPositive,
      }"
    >
      <div
        slot="title"
        class="title subscriptionTitle"
        :class="`${$mq}Mode`"
      >
        <el-row
          :gutter="2"
          type="flex"
        >
          <el-col :span="responsiveCol.one">
            <span v-if="hasInviteResetNegative">
              {{ $t('customAlert.subscription.subscriptionNegativeAlert') }}
            </span>
            <span v-if="hasInviteResetPositive">
              {{ $t('customAlert.subscription.subscriptionPositiveAlert') }}
            </span>
          </el-col>

          <el-col
            class="buttonCol"
            :span="responsiveCol.two"
          >
            <el-button
              round
              @click="showSubscriptionDialog = true"
            >
              {{ $t('common.update') }}
            </el-button>
          </el-col>
        </el-row>
      </div>
    </el-alert>

    <!-- Subscription payment -->
    <el-alert
      v-if="tempSubAlert"
      show-icon
      type="warning"
      class="mainCard subscriptionAlert subscriptionNegativeAlert defaultCursor"
      :closable="false"
    >
      <div
        slot="title"
        class="title subscriptionTitle"
        :class="`${$mq}Mode`"
      >
        <el-row
          :gutter="2"
          type="flex"
        >
          <el-col :span="responsiveCol.one">
            <span>
              {{ $t('customAlert.subscription.subscriptionComingSoon') }}
            </span>
          </el-col>

          <el-col
            class="buttonCol"
            :span="responsiveCol.two"
          >
            <el-button
              round
              @click="goToPlans"
            >
              {{ $t('common.seePlans') }}
            </el-button>
          </el-col>
        </el-row>
      </div>
    </el-alert>

    <el-alert
      v-else-if="subscriptionStatus === 'failed'"
      show-icon
      type="warning"
      class="mainCard subscriptionAlert subscriptionNegativeAlert defaultCursor"
    >
      <div
        slot="title"
        class="title subscriptionTitle"
        :class="`${$mq}Mode`"
      >
        <el-row
          :gutter="2"
          type="flex"
        >
          <el-col :span="responsiveCol.one">
            <span>
              {{ $t('customAlert.subscription.subscriptionPaymentAlert') }}
            </span>
          </el-col>

          <el-col
            class="buttonCol"
            :span="responsiveCol.two"
          >
            <el-button
              round
              @click="goToPayment"
            >
              {{ $t('common.update') }}
            </el-button>
          </el-col>
        </el-row>
      </div>
    </el-alert>

    <el-alert
      v-else-if="subscriptionStatus === 'no_subscription'"
      show-icon
      type="warning"
      class="mainCard subscriptionAlert subscriptionNegativeAlert defaultCursor"
    >
      <div
        slot="title"
        class="title subscriptionTitle"
        :class="`${$mq}Mode`"
      >
        <el-row
          :gutter="2"
          type="flex"
        >
          <el-col :span="responsiveCol.one">
            <span>
              {{ $t('customAlert.subscription.subscriptionNoPlanAlert') }}
            </span>
          </el-col>

          <el-col
            class="buttonCol"
            :span="responsiveCol.two"
          >
            <el-button
              round
              @click="goToPlans"
            >
              {{ $t('common.seePlans') }}
            </el-button>
          </el-col>
        </el-row>
      </div>
    </el-alert>

    <CoachCancellationDialog
      :show.sync="showCancellationModal"
      :cancellation="selectedCancellation"
    />

    <SubscriptionDialog :show.sync="showSubscriptionDialog" />
  </div>
</template>

<script>
import { mapState, mapGetters, mapActions } from 'vuex';
import CoachCancellationDialog from './CoachCancellationDialog.vue';
import SubscriptionDialog from './component/SubscriptionDialog.vue';

export default {
  components: {
    CoachCancellationDialog,
    SubscriptionDialog,
  },

  data() {
    return {
      showAttendancesList: false,
      showCancellationsList: false,
      showPulseList: false,
      showCancellationModal: false,
      selectedCancellation: null,
      showSubscriptionDialog: false,
    };
  },

  computed: {
    ...mapState({
      attendances: (state) => state.coachAlerts.attendance,
      cancellations: (state) => state.coachAlerts.cancellation,
      pulseLaunch: (state) => state.coachAlerts.pulseLaunch,
      profile: (state) => state.user.profile,
      inviteResetPositive: (state) => state.coachAlerts.inviteResetPositive,
      inviteResetNegative: (state) => state.coachAlerts.inviteResetNegative,
      subscriptionStatus: (state) => state.coachAlerts.subscriptionStatus,
      tempSubAlert: (state) => state.coachAlerts.tempSubAlert,
    }),

    ...mapGetters('coachAlerts', {
      hasAttendances: 'hasAttendances',
      hasCancellations: 'hasCancellations',
      hasPulseLaunch: 'hasPulseLaunch',
      hasInviteResetPositive: 'hasInviteResetPositive',
      hasInviteResetNegative: 'hasInviteResetNegative',
    }),

    responsiveCol() {
      const col = {};
      if (this.$mq === 'mobile') {
        col.one = 24;
        col.two = 24;
      } else if (this.$mq === 'tablet') {
        col.one = 18;
        col.two = 6;
      } else {
        col.one = 20;
        col.two = 4;
      }
      return col;
    },
  },

  mounted() {
    // from CoahCancellationDialog - Cancellation
    // from AttendanceModal - Attendance
    this.$EventBus.$on('alert_changed', (data) => {
      this.alertChanged(data);
    });
  },

  methods: {
    ...mapActions('coachAlerts', [
      'alertChanged',
    ]),

    goToLeader(uuid, type) {
      if (this.$route.name !== 'myLeaderDetail' || this.$route.params.id !== uuid) {
        this.$router.push(
          {
            name: 'myLeaderDetail',
            params: { id: uuid },
          },
        ).catch((err) => { console.log(err); });
      } else {
        // from Tasks - open_coachability
        // from SessionAttendance - open_attendance
        this.$EventBus.$emit(`open_${type}`, type);
      }
    },

    goToPulse(uuid) {
      this.$router.push(
        {
          name: 'myLeaderPulseDetail',
          params: { id: uuid },
        },
      ).catch((err) => { console.log(err); });
    },

    handleCancellationClick(cancellation) {
      this.showCancellationModal = true;
      this.selectedCancellation = cancellation;
    },

    goToCoachProfile() {
      const client = process.env.VUE_APP_COACH_CLIENT;
      window.location.href = `${client}/dashboard/profile`;
    },

    goToPayment() {
      let url = 'https://gcgroup-test.chargebeeportal.com/portal/v2/login?forward=home';
      if (process.env.VUE_APP_ENV === 'prod') {
        url = 'https://gcgroup.chargebeeportal.com/';
      }
      window.open(url, '_blank');
    },

    goToPlans() {
      const url = 'https://globalcoachgroup.com/coaching-certification-fees/all-in-one-coaching-toolkit';
      window.open(url, '_blank');
    },
  },
};
</script>

<style lang="scss" scoped>
#coachAlert {
  .el-alert {
    color: #000;
    margin-bottom: 20px !important;
    display: flex;
    align-items: flex-start;

    &.profileAlert {
      background-color: #ffbf60;
    }

    &.attendancesAlert {
      background-color: #a3d2ec;
    }

    &.cancellationsAlert {
      background-color: #bbf2d0;
    }

    &.pulseAlert {
      background-color: #72fedc;
    }

    &.subscriptionNegativeAlert {
      background-color: #ff8743;
    }

    &.subscriptionPositiveAlert {
      background-color: #68c558;
    }
  }

  /* stylelint-disable-line */
  .el-alert__content {
    .title {
      display: flex;
      justify-content: space-around;
      font-size: 14px;
      font-weight: normal;
      padding-top: 5px;
      line-height: 42px;
      padding-right: 10px;

      &.profileAlertTitle {
        line-height: 22px;
        padding-top: 10px;
        padding-bottom: 5px;
      }

      &.subscriptionTitle {
        line-height: unset;
        padding: 15px 0;
      }

      .el-row {
        width: 100%;
      }

      span {
        align-self: center;
      }

      .buttonCol {
        text-align: right;

        .el-button {
          background: transparent;
          color: #000;
          border-color: #000;
          font-size: 14px;
          width: 100%;
          padding: 13px 0;
        }
      }

      &.tabletMode {
        line-height: 20px;
      }

      &.mobileMode {
        line-height: 20px;

        .el-row {
          flex-wrap: wrap;

          .el-col:first-child {
            margin-bottom: 10px;
          }
        }
      }
    }

    .content {
      color: #000;
      font-size: 14px;

      ul {
        list-style: none;
        padding-left: 0;
        margin-top: 5px;

        li {
          cursor: pointer;
          width: fit-content;
          text-decoration: underline;

          &:not(:first-child) {
            margin-top: 10px;
          }
        }
      }
    }
  }

  &.mobileMode {
    .el-alert {
      align-items: flex-start;
    }
  }
}
</style>

<style lang="scss">
#coachAlert {
  .profileAlert {
    .el-alert__icon {
      font-size: 45px;
    }

    .el-alert__content {
      padding-left: 45px !important;
    }
  }

  .pulseAlert {
    .el-alert__closebtn {
      font-size: 15px;
      color: #000;
    }
  }

  .subscriptionAlert {
    .el-alert__closebtn {
      font-size: 15px;
      color: #000;
    }

    .el-alert__icon {
      font-size: 30px;
    }
  }

  .el-alert {
    .el-alert__icon {
      padding-top: 12px;
    }

    .el-alert__content {
      width: 100%;
      padding-left: 20px;
    }
  }

  &.mobileMode {
    .el-alert {
      .el-alert__icon {
        padding: 10px 0 0;
      }
    }
  }
}
</style>
